/**
 * 카드·표·목록이 함께 쓰는 조각들.
 *
 * **규칙 하나: 안 정한 것은 아무것도 안 그린다.** 회색 「없음」 칩을 그리면
 * 카드마다 빈칸이 늘어서 보드가 시끄러워지고, 그 칩은 아무 정보도 없다.
 * 그래서 아래 컴포넌트는 값이 `null` 이면 **`null` 을 돌려준다.**
 */

import type { Label, LabelColor, TicketPriority, TicketType, UserBrief } from '../api';
import { labelVars, priorityOf, typeOf } from '../columns';

/**
 * **배지 한 벌.** 티켓 번호·유형·우선순위·마감·라벨·발생 환경이 전부 이
 * 꼴을 쓴다(2026-08-29 지시: 「티켓 번호처럼 뱃지로 만들어라」).
 *
 * 꼴이 하나여야 **위계를 색이 진다.** 어떤 값은 알약이고 어떤 값은 맨
 * 글자이면, 크기와 배경이 제각각이라 눈이 무엇부터 볼지 못 정한다 —
 * 카드에서 실제로 그랬다.
 *
 * ⚠ **여기에 색을 박지 마라.** 색은 부르는 쪽이 `toneVars` 로 준다.
 *
 * ⚠ **`whitespace-nowrap` 을 빼지 마라.** 폭이 정해진 자리(목록 뷰의 번호 열)에서
 * 배지 안쪽 여백에 밀려 「viewster-63」이 **두 줄로 쪼개졌다**(실측).
 */
export const BADGE =
  'inline-flex shrink-0 items-center gap-s1 whitespace-nowrap rounded-sm px-s2 py-[1px] text-micro leading-[1.5]';

/**
 * 배지 색 한 벌. 옅은 바탕(`-t`)에 진한 글자(`-f`).
 *
 * ⚠ **접미사를 뒤집어 쓰지 마라** — `-f` 를 바탕에 쓰면 어두운 테마 다섯에서
 * 글자가 바탕에 묻힌다.
 */
export function toneVars(tone: LabelColor): React.CSSProperties {
  return { background: `var(--st-${tone}-t)`, color: `var(--st-${tone}-f)` };
}

/**
 * 이슈 타입 — **이름이 그대로 보인다.** 옛 판은 도형 하나였고, 그 도형이
 * 무엇을 뜻하는지 알 길이 툴팁뿐이었다(손가락으로 쓰는 화면에서는 그것도
 * 안 뜬다). 자세한 사정은 `columns.ts` 의 `TypeDef` 주석에 있다.
 */
export function TypeMark({ type }: { type: TicketType | null }) {
  const def = typeOf(type);
  if (!def) return null;
  return (
    <span className={BADGE} style={toneVars(def.tone)}>
      {def.label}
    </span>
  );
}

/**
 * 우선순위 — 화살표로 방향을, 글자로 이름을 읽는다. **색만으로 가르지
 * 않는다**(색각 이상에서 붉음/노랑이 붙는다).
 */
export function PriorityMark({ priority }: { priority: TicketPriority | null }) {
  const def = priorityOf(priority);
  if (!def) return null;
  return (
    <span className={BADGE} style={toneVars(def.tone)}>
      <span aria-hidden="true">{def.mark}</span>
      {def.label}
    </span>
  );
}

/**
 * 라벨 하나. 색은 `styles.css` 의 상태색 토큰에서 온다.
 *
 * `muted` 는 **고를 수 있지만 아직 안 고른** 상태다. 투명도만 낮추면
 * 켜짐과 꺼짐이 비슷해 보여서(실측), 색을 아예 빼고 회색 윤곽으로 만든다 —
 * 색의 유무가 가장 읽기 쉬운 켜짐 신호다.
 */
export function LabelChip({ label, muted }: { label: Label; muted?: boolean }) {
  return (
    <span
      className={`${BADGE} max-w-[120px] truncate border`}
      style={
        muted
          ? {
              background: 'transparent',
              color: 'var(--ink-3)',
              borderColor: 'var(--line)',
            }
          : labelVars(label.color)
      }
    >
      {label.name}
    </span>
  );
}

/**
 * 아바타 색을 고르는 여섯. **사람마다 달라야 카드를 훑을 때 담당자가
 * 읽힌다** — 전부 브랜드색이면 글자를 하나하나 봐야 한다.
 */
const AVATAR_TONES = ['blue', 'violet', 'green', 'amber', 'red', 'gray'] as const;

/**
 * 사람 id 로 색을 고른다. **이름이 아니라 id 다** — 닉네임을 바꿨다고
 * 아바타 색이 변하면 「다른 사람이 됐다」로 읽힌다.
 */
function toneOf(userId: number): string {
  return AVATAR_TONES[userId % AVATAR_TONES.length];
}

/**
 * 담당자 — 아바타 하나. **글자는 서버가 잘라 준다**(`initial`) — 화면마다
 * 자르는 규칙이 다르면 같은 사람이 화면마다 다른 글자로 보인다.
 */
export function AssigneeMark({
  user,
  size = 20,
}: {
  user: UserBrief | null;
  size?: number;
}) {
  if (!user) return null;
  const tone = toneOf(user.id);

  /*
    **사진이 있으면 사진이다**(2026-08-29 지시). 이니셜 한 글자 + 색 여섯은
    사람이 늘면 갈리지 않는다 — 같은 글자로 시작하는 이름이 둘이면 색으로만
    갈리고, 일곱을 넘으면 그 색도 겹친다.

    ⚠ **자리를 그대로 쓴다.** 테두리(`boxShadow`)와 크기가 이니셜일 때와
    같아야 카드를 훑을 때 오른쪽 끝이 세로로 곧게 내려간다.

    ⚠ **`title` 을 둘 다에 건다.** 사진만으로는 누구인지 확신이 안 서는
    경우가 있고, 그때 이름을 볼 길이 이것뿐이다.
  */
  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={`담당 ${user.display_name}`}
        title={user.display_name}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className="shrink-0 rounded-full object-cover"
        style={{
          width: size,
          height: size,
          boxShadow: 'inset 0 0 0 1px var(--line)',
          background: 'var(--sunk)',
        }}
      />
    );
  }

  return (
    <span
      className="inline-grid shrink-0 place-items-center rounded-full text-[10px] font-bold"
      style={{
        width: size,
        height: size,
        background: `var(--st-${tone}-t)`,
        color: `var(--st-${tone}-f)`,
        boxShadow: `inset 0 0 0 1px var(--st-${tone}-b)`,
      }}
      title={user.display_name}
    >
      <span aria-hidden="true">{user.initial}</span>
      <span className="sr-only">담당 {user.display_name}</span>
    </span>
  );
}

/**
 * 마감일.
 *
 * **지난 것만 붉다.** 전부 붉히면 급한 것이 안 보인다 — 오늘까지는 다른
 * 배지와 같은 회색이고, 지나면 깃발이 붙는다.
 */
export function DueMark({ due }: { due: string | null }) {
  if (!due) return null;
  const today = new Date().toISOString().slice(0, 10);
  const late = due < today;
  const text = due.slice(5).replace('-', '/');
  return (
    <span
      className={`${BADGE} tnum`}
      style={toneVars(late ? 'red' : 'gray')}
      title={late ? `${due} — 지났다` : due}
    >
      {late && <span aria-hidden="true">⚑</span>}
      {text}
    </span>
  );
}

